import type * as schemas from "@common/schemas/v2";
import * as models from "@common/models/v2";
import { BaseStep } from "./base";
import { openNewTab, openNewWindow, openSameTab } from "@/lib/window";
import { ElementClicker } from "@/lib/element";
import { StepActions } from "./step";
import { makeObservable, observable, override } from "mobx";
import { FlowFlexibility } from "@common/schemas";

export type QuestionStep = MultipleChoiceQuestionStep;
export type QuestionChoice =
  | LinkChoice
  | ProceedNextChoice
  | SuspendTourChoice
  | FinishTourChoice
  | DismissTourChoice
  | TriggerClickChoice
  | SwitchTourChoice
  | ContactSupportChoice
  | LoadCheckpointChoice;

export class MultipleChoiceQuestionStep extends BaseStep {
  attributes: models.MultipleChoiceQuestionStepAttributes;
  actions: StepActions;

  choices: QuestionChoice[];
  isAnswered = false;
  isDisposed = false;
  allowProceed = false;

  constructor(step: schemas.Step, actions: StepActions, flowFlexibility: FlowFlexibility) {
    super(step, actions, flowFlexibility);
    this.attributes = new models.MultipleChoiceQuestionStepAttributes(
      step.attributes as schemas.MultipleChoiceQuestionStepAttributes
    );
    this.actions = actions;
    this.choices = QuestionChoiceFactory.createList(this.attributes.choices, actions);
    makeObservable(this, {
      isAnswered: observable,
      isDisposed: observable,
      allowProceed: observable,
      isBlocking: override,
    });
  }

  async run() {
    super.run();
    this.isAnswered = false;
    this.isDisposed = false;
    this.allowProceed = false;
  }

  async finish() {
    super.finish();
  }

  async answer(choiceId: string) {
    const choice = this.choices.find((c) => c.id === choiceId);
    if (!choice) {
      return;
    }
    super.emitter.emit("questionAnswered", { choice });
    this.isAnswered = true;

    const { allowProceed } = await choice.execute();
    this.allowProceed = allowProceed;
  }

  dispose(): void {
    super.dispose();
    this.isDisposed = true;
  }

  get prompt() {
    if (this.isAnswered || this.isDisposed) {
      return null;
    }

    return {
      type: "multipleChoice",
      title: this.attributes.title,
      text: this.attributes.text,
      choices: this.choices,
    };
  }

  get additionalContent() {
    return this.attributes?.additionalContent;
  }

  get isBlocking() {
    return !this.allowProceed;
  }

  get isBlockingStepType() {
    return true;
  }

  get skipStepOnEarlyUnblock() {
    return true;
  }
}

export class DismissTourChoice {
  id: string;
  text: string;
  actions: StepActions;
  buttonVariant: schemas.ChoiceButtonVariant = "secondary";

  constructor(choice: schemas.DismissTourChoice, actions: StepActions) {
    const dismissTourChoice = new models.DismissTourChoice(choice);
    this.id = dismissTourChoice.id;
    this.text = dismissTourChoice.text;
    this.actions = actions;
    this.buttonVariant = dismissTourChoice.buttonVariant;
  }

  execute() {
    this.actions.dismissTour();
    return { allowProceed: false };
  }
}

export class LinkChoice {
  id: string;
  text: string;
  windowType: schemas.LinkChoiceWindowType;
  url: string;
  actions: StepActions;
  buttonVariant: schemas.ChoiceButtonVariant = "primary";

  constructor(choice: schemas.LinkChoice, actions: StepActions) {
    const linkChoice = new models.LinkChoice(choice);

    this.id = linkChoice.id;
    this.text = linkChoice.text;
    this.windowType = linkChoice.windowType;
    this.url = linkChoice.url;
    this.actions = actions;
    this.buttonVariant = linkChoice.buttonVariant;
  }

  execute() {
    switch (this.windowType) {
      case "newTab":
        openNewTab(this.url);
        break;
      case "sameTab":
        openSameTab(this.url);
        break;
      case "newWindow":
        openNewWindow(this.url);
        break;
      default:
        openNewTab(this.url);
        break;
    }

    return { allowProceed: true };
  }
}

export class SwitchTourChoice {
  id: string;
  type = "switchTour";
  text: string;
  guideId: string;
  actions: StepActions;
  buttonVariant: schemas.ChoiceButtonVariant = "primary";

  constructor(choice: schemas.SwitchTourChoice, actions: StepActions) {
    const switchTourChoice = new models.SwitchTourChoice(choice);
    this.id = switchTourChoice.id;
    this.text = switchTourChoice.text;
    this.guideId = switchTourChoice.guideId;
    this.actions = actions;
    this.buttonVariant = switchTourChoice.buttonVariant;
  }

  execute() {
    this.actions.switchTour(this.guideId);
    return { allowProceed: false };
  }
}

export class ProceedNextChoice {
  id: string;
  type = "proceedNext";
  text: string;
  actions: StepActions;
  buttonVariant: schemas.ChoiceButtonVariant = "primary";

  constructor(choice: schemas.ProceedNextChoice, actions: StepActions) {
    const proceedNextChoice = new models.ProceedNextChoice(choice);
    this.id = proceedNextChoice.id;
    this.text = proceedNextChoice.text;
    this.actions = actions;
    this.buttonVariant = proceedNextChoice.buttonVariant;
  }

  execute() {
    return { allowProceed: true };
  }
}

export class SuspendTourChoice {
  id: string;
  type = "suspendTour";
  text: string;
  actions: StepActions;
  buttonVariant: schemas.ChoiceButtonVariant = "primary";

  constructor(choice: schemas.SuspendTourChoice, actions: StepActions) {
    const suspendTourChoice = new models.SuspendTourChoice(choice);
    this.id = suspendTourChoice.id;
    this.text = suspendTourChoice.text;
    this.actions = actions;
    this.buttonVariant = suspendTourChoice.buttonVariant;
  }

  execute() {
    this.actions.suspendTour();
    return { allowProceed: false };
  }
}

export class FinishTourChoice {
  id: string;
  type = "finishTour";
  text: string;
  actions: StepActions;
  buttonVariant: schemas.ChoiceButtonVariant = "primary";
  constructor(choice: schemas.FinishTourChoice, actions: StepActions) {
    const finishTourChoice = new models.FinishTourChoice(choice);
    this.id = finishTourChoice.id;
    this.text = finishTourChoice.text;
    this.actions = actions;
    this.buttonVariant = finishTourChoice.buttonVariant;
  }

  execute() {
    this.actions.finishTour();
    return { allowProceed: false };
  }
}

export class TriggerClickChoice {
  id: string;
  type = "triggerClick";
  text: string;
  selector: string;
  actions: StepActions;
  buttonVariant: schemas.ChoiceButtonVariant = "primary";

  constructor(choice: schemas.TriggerClickChoice, actions: StepActions) {
    const triggerClickChoice = new models.TriggerClickChoice(choice);
    this.id = triggerClickChoice.id;
    this.text = triggerClickChoice.text;
    this.selector = triggerClickChoice.selector;
    this.actions = actions;
    this.buttonVariant = triggerClickChoice.buttonVariant;
  }

  async execute() {
    const elementClicker = new ElementClicker();
    await elementClicker.clickElement(this.selector);
    return { allowProceed: true };
  }
}

export class ContactSupportChoice {
  id: string;
  type = "contactSupport";
  text: string;
  url: string;
  actions: StepActions;
  buttonVariant: schemas.ChoiceButtonVariant = "primary";

  constructor(choice: schemas.ContactSupportChoice, actions: StepActions) {
    const contactSupportChoice = new models.ContactSupportChoice(choice);
    this.id = contactSupportChoice.id;
    this.text = contactSupportChoice.text;
    this.url = contactSupportChoice.url;
    this.actions = actions;
    this.buttonVariant = contactSupportChoice.buttonVariant;
  }

  execute() {
    openNewTab(this.url);
    return { allowProceed: true };
  }
}

export class LoadCheckpointChoice {
  id: string;
  type = "loadCheckpoint";
  text: string;
  actions: StepActions;
  buttonVariant: schemas.ChoiceButtonVariant = "primary";

  constructor(choice: schemas.LoadCheckpointChoice, actions: StepActions) {
    const loadCheckpointChoice = new models.LoadCheckpointChoice(choice);
    this.id = loadCheckpointChoice.id;
    this.text = loadCheckpointChoice.text;
    this.actions = actions;
    this.buttonVariant = loadCheckpointChoice.buttonVariant;
  }

  execute() {
    this.actions.loadLastCheckpoint();
    return { allowProceed: false };
  }
}

export class QuestionFactory {
  static create(step: schemas.QuestionStep, actions: StepActions, flowFlexibility: FlowFlexibility): QuestionStep {
    switch (step.attributes.questionType) {
      case "multipleChoice":
        return new MultipleChoiceQuestionStep(step, actions, flowFlexibility);
      default:
        throw new Error(`Unknown question type: ${(step as schemas.QuestionStep).attributes.questionType}`);
    }
  }
}

export class QuestionChoiceFactory {
  static create(choice: schemas.QuestionChoice, actions: StepActions): QuestionChoice {
    switch (choice.type) {
      case "link":
        return new LinkChoice(choice as schemas.LinkChoice, actions);
      case "proceedNext":
        return new ProceedNextChoice(choice as schemas.ProceedNextChoice, actions);
      case "suspendTour":
        return new SuspendTourChoice(choice as schemas.SuspendTourChoice, actions);
      case "finishTour":
        return new FinishTourChoice(choice as schemas.FinishTourChoice, actions);
      case "switchTour":
        return new SwitchTourChoice(choice as schemas.SwitchTourChoice, actions);
      case "triggerClick":
        return new TriggerClickChoice(choice as schemas.TriggerClickChoice, actions);
      case "contactSupport":
        return new ContactSupportChoice(choice as schemas.ContactSupportChoice, actions);
      case "loadCheckpoint":
        return new LoadCheckpointChoice(choice as schemas.LoadCheckpointChoice, actions);
      case "dismissTour":
        return new DismissTourChoice(choice as schemas.DismissTourChoice, actions);
      default:
        throw new Error(`Unknown question choice type: ${(choice as any).type}`);
    }
  }

  static createList(choices: schemas.QuestionChoice[], actions: StepActions): QuestionChoice[] {
    if (choices.length === 0) {
      const defaultChoice = new ProceedNextChoice(
        { id: "defaultChoice", type: "proceedNext", text: "Continue", buttonVariant: "primary" },
        actions
      );
      return [defaultChoice];
    }

    return choices.map((choice) => this.create(choice, actions));
  }
}

export function isTourSwitchChoice(choice: any): choice is SwitchTourChoice {
  return choice.type === "switchTour" && "guideId" in choice;
}
